import { useTranslation } from 'react-i18next';
import { CopyIcon, EraserIcon, HashIcon, Trash2Icon } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';

interface BulkToolbarProps {
  count: number;
  onDuplicate: () => void;
  onClear: () => void;
  onDelete: () => void;
  /** Absent when the schema declares no tracking-code field. */
  onGenerateCodes?: () => void;
  generatingCodes?: boolean;
  /** Why minting is unavailable right now. Disables the button and explains it. */
  codesBlockedReason?: string;
  /**
   * A clear or a delete is still in flight.
   *
   * Both are irreversible, and starting a second before the first settles is not
   * merely wasteful: `MutationObserver.mutate` detaches the observer from the
   * mutation still running, so the first call's per-call `onSuccess` - the one
   * that drops the local edits it just cleared and reports its conflicts - never
   * fires. The cleared cell then re-displays the value the user confirmed
   * clearing, because the stale patch is merged back over the refetch.
   */
  busy?: boolean;
}

/**
 * Actions on the selected records.
 *
 * Sticks to the top of the grid and PUSHES the header down rather than
 * overlaying it, matching the prototype — an overlay hides the column a user is
 * about to act on.
 *
 * Copy and Paste are absent on purpose: they are native clipboard events on the
 * grid itself, so they work against a cell RANGE and interoperate with Excel.
 * Buttons here would only be able to offer whole rows, which is the weaker of
 * the two behaviours.
 */
export function BulkToolbar({
  count,
  onDuplicate,
  onClear,
  onDelete,
  onGenerateCodes,
  generatingCodes,
  codesBlockedReason,
  busy = false,
}: BulkToolbarProps) {
  const { t } = useTranslation('knowledge');

  return (
    /*
      `h-4xl` is the design system's 48px step. Height is fixed rather than derived
      from padding so the bar does not resize when `codesBlockedReason` adds a line
      of text beside the mint button.
    */
    <div className="sticky top-0 z-40 flex h-4xl items-center gap-sm border-b border-border-light bg-surface px-md">
      <span className="text-sm font-medium">{t('taxonomy.grid.selected', { count })}</span>
      {/* First, and the only one that is not `ghost`: it is the only action here
          that spends something permanent, so it should not read as peer to
          Clear and Delete. The divider after it carries that separation now that
          the actions are left-aligned and no spacer sits between them. */}
      {onGenerateCodes && (
        <Button
          variant="outline"
          size="sm"
          onClick={onGenerateCodes}
          disabled={generatingCodes || Boolean(codesBlockedReason)}
          // The reason travels with the disabled state. A button that is simply
          // dead reads as broken, and this one is disabled for a fixable cause.
          title={codesBlockedReason}
        >
          <HashIcon className="size-4" />
          {t('taxonomy.grid.generateCodes')}
        </Button>
      )}
      {codesBlockedReason && (
        <span className="text-xs text-muted-foreground">{codesBlockedReason}</span>
      )}
      {onGenerateCodes && <span aria-hidden className="h-5 w-px shrink-0 bg-border-light" />}
      <Button variant="ghost" size="sm" onClick={onDuplicate}>
        <CopyIcon className="size-4" />
        {t('taxonomy.grid.duplicate')}
      </Button>
      {/*
        Clear and Delete read as one destructive pair: both empty work the user
        typed, and only Duplicate is additive.

        The hover pair is Tessra's, not the brief's. `destructive-soft` is the ROSE
        ramp and the library pairs it with `destructive-soft-foreground` (rose-800),
        while the brief put rose-600 text on it. Tokens rather than the hex, because
        the dark and Metropolis themes redefine both.
      */}
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive-soft-foreground hover:bg-destructive-soft"
        disabled={busy}
        onClick={onClear}
      >
        <EraserIcon className="size-4" />
        {t('taxonomy.grid.clearAction')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive-soft-foreground hover:bg-destructive-soft"
        disabled={busy}
        onClick={onDelete}
      >
        <Trash2Icon className="size-4" />
        {t('taxonomy.grid.deleteAction')}
      </Button>
    </div>
  );
}
